/**
 * Leep Chás — Stories modal player
 */
const STORIES_FOCUSABLE =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

class LeepStoriesModal extends HTMLElement {
  constructor() {
    super();
    this.stories = [];
    this.index = 0;
    this.isOpen = false;
    this.isPaused = false;
    this.longPressTimer = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.lastActiveElement = null;
    this.releaseFocusTrap = null;
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onTimeUpdate = this.onTimeUpdate.bind(this);
    this.onVideoEnded = this.onVideoEnded.bind(this);
  }

  connectedCallback() {
    this.backdrop = this.querySelector('[data-stories-backdrop]');
    this.dialog = this.querySelector('[data-stories-dialog]');
    this.progressEl = this.querySelector('[data-stories-progress]');
    this.video = this.querySelector('[data-stories-video]');
    this.poster = this.querySelector('[data-stories-poster]');
    this.titleEl = this.querySelector('[data-stories-title]');
    this.cta = this.querySelector('[data-stories-cta]');
    this.muteBtn = this.querySelector('[data-stories-mute]');
    this.closeBtn = this.querySelector('[data-stories-close]');
    this.tapPrev = this.querySelector('[data-stories-tap-prev]');
    this.tapNext = this.querySelector('[data-stories-tap-next]');

    this.loadStories();

    this.closeBtn?.addEventListener('click', () => this.close());
    this.backdrop?.addEventListener('click', () => this.close());
    this.muteBtn?.addEventListener('click', () => this.toggleMute());
    this.tapPrev?.addEventListener('click', () => this.prev());
    this.tapNext?.addEventListener('click', () => this.next());
    this.cta?.addEventListener('click', () => this.close());

    this.video?.addEventListener('timeupdate', this.onTimeUpdate);
    this.video?.addEventListener('ended', this.onVideoEnded);

    this.bindTouch();
    this.bindLongPress();

    document.addEventListener('leep:stories-open', (event) => {
      const index = event.detail?.index ?? 0;
      this.open(index);
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-stories-open]');
      if (!trigger) return;
      event.preventDefault();
      const index = parseInt(trigger.dataset.storiesIndex || '0', 10);
      this.open(Number.isNaN(index) ? 0 : index);
    });
  }

  loadStories() {
    const dataEl = this.querySelector('[data-stories-json]');
    if (!dataEl) return;
    try {
      const parsed = JSON.parse(dataEl.textContent);
      this.stories = Array.isArray(parsed) ? parsed.filter((s) => s.sources?.length) : [];
    } catch {
      this.stories = [];
    }
  }

  bindTouch() {
    const stage = this.querySelector('[data-stories-stage]');
    if (!stage) return;

    stage.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.touches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.touchStartTime = Date.now();
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      (event) => {
        if (this.longPressTimer) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - this.touchStartX;
        const dy = touch.clientY - this.touchStartY;
        const dt = Date.now() - this.touchStartTime;

        if (Math.abs(dy) > 80 && dy > 0 && Math.abs(dy) > Math.abs(dx)) {
          this.close();
          return;
        }

        if (dt < 300 && Math.abs(dx) > 50) {
          if (dx < 0) this.next();
          else this.prev();
        }
      },
      { passive: true }
    );
  }

  bindLongPress() {
    const stage = this.querySelector('[data-stories-stage]');
    if (!stage || !this.video) return;

    const pause = () => {
      if (!this.isOpen) return;
      this.isPaused = true;
      this.video.pause();
    };

    const resume = () => {
      if (!this.isOpen || !this.isPaused) return;
      this.isPaused = false;
      this.video.play().catch(() => {});
    };

    stage.addEventListener('pointerdown', () => {
      this.longPressTimer = window.setTimeout(pause, 280);
    });

    ['pointerup', 'pointerleave', 'pointercancel'].forEach((type) => {
      stage.addEventListener(type, () => {
        if (this.longPressTimer) {
          clearTimeout(this.longPressTimer);
          this.longPressTimer = null;
        }
        resume();
      });
    });
  }

  buildProgress() {
    if (!this.progressEl) return;
    this.progressEl.innerHTML = '';
    this.stories.forEach((_, i) => {
      const segment = document.createElement('div');
      segment.className = 'stories-modal__progress-segment';
      segment.setAttribute('role', 'presentation');
      const fill = document.createElement('span');
      fill.className = 'stories-modal__progress-fill';
      fill.dataset.index = String(i);
      segment.appendChild(fill);
      this.progressEl.appendChild(segment);
    });
  }

  updateProgress() {
    if (!this.video || !this.progressEl) return;
    const fills = this.progressEl.querySelectorAll('.stories-modal__progress-fill');
    fills.forEach((fill, i) => {
      const segment = fill.parentElement;
      if (i < this.index) {
        fill.style.width = '100%';
        segment?.classList.add('is-complete');
      } else if (i > this.index) {
        fill.style.width = '0%';
        segment?.classList.remove('is-complete');
      } else {
        segment?.classList.remove('is-complete');
        const duration = this.video.duration;
        const pct = duration && Number.isFinite(duration) ? (this.video.currentTime / duration) * 100 : 0;
        fill.style.width = `${pct}%`;
      }
    });
  }

  onTimeUpdate() {
    this.updateProgress();
  }

  onVideoEnded() {
    if (this.index < this.stories.length - 1) {
      this.next();
    } else {
      this.close();
    }
  }

  showStory(index) {
    const story = this.stories[index];
    if (!story || !this.video) return;

    this.index = index;
    this.isPaused = false;

    // Remove listener pendente de story anterior para evitar reprodução indevida
    if (this._pendingPlay) {
      this.video.removeEventListener('canplay', this._pendingPlay);
      this._pendingPlay = null;
    }

    this.video.pause();
    this.video.removeAttribute('src');
    this.video.innerHTML = '';

    story.sources.forEach((source) => {
      const sourceEl = document.createElement('source');
      sourceEl.src = source.url;
      if (source.mimeType) sourceEl.type = source.mimeType;
      this.video.appendChild(sourceEl);
    });

    if (this.poster) {
      if (story.poster) {
        this.poster.src = story.poster;
        this.poster.hidden = false;
      } else {
        this.poster.hidden = true;
      }
    }

    if (this.titleEl) {
      this.titleEl.textContent = story.title || '';
      this.titleEl.hidden = !story.title;
    }

    if (this.cta) {
      if (story.ctaUrl && story.ctaLabel) {
        this.cta.href = story.ctaUrl;
        this.cta.textContent = story.ctaLabel;
        this.cta.hidden = false;
      } else {
        this.cta.hidden = true;
      }
    }

    this.updateProgress();
    this.video.muted = true;
    this.updateMuteButton();

    // Registra canplay ANTES de load() para evitar race condition onde
    // o evento dispara antes do catch de AbortError conseguir adicionar o listener.
    this._pendingPlay = () => {
      this._pendingPlay = null;
      if (this.poster) this.poster.hidden = true;
      this.video.play().catch(() => {});
    };
    this.video.addEventListener('canplay', this._pendingPlay, { once: true });

    this.video.load();
  }

  open(index = 0) {
    const emptyEl = this.querySelector('[data-stories-empty]');
    const tapZones = this.querySelector('.stories-modal__tap-zones');

    if (!this.stories.length) {
      this.isOpen = true;
      this.hidden = false;
      this.classList.add('is-open');
      this.lastActiveElement = document.activeElement;
      this.lockScroll(true);
      this.releaseFocusTrap?.();
      this.releaseFocusTrap = window.LeepFocusTrap?.activate(this.dialog) || null;
      emptyEl?.removeAttribute('hidden');
      this.video?.setAttribute('hidden', '');
      tapZones?.setAttribute('hidden', '');
      document.addEventListener('keydown', this.onKeyDown);
      this.closeBtn?.focus();
      return;
    }

    emptyEl?.setAttribute('hidden', '');
    this.video?.removeAttribute('hidden');
    tapZones?.removeAttribute('hidden');

    const safeIndex = Math.max(0, Math.min(index, this.stories.length - 1));
    this.lastActiveElement = document.activeElement;
    this.isOpen = true;
    this.hidden = false;
    this.removeAttribute('aria-hidden');
    this.classList.add('is-open');
    this.lockScroll(true);
    this.releaseFocusTrap?.();
    this.releaseFocusTrap = window.LeepFocusTrap?.activate(this.dialog) || null;
    this.buildProgress();
    this.showStory(safeIndex);
    document.addEventListener('keydown', this.onKeyDown);
    this.closeBtn?.focus();

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduced && typeof gsap !== 'undefined') {
      gsap.fromTo(
        this.dialog,
        { scale: 0.92, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.35, ease: 'expo.out' }
      );
    }
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.classList.remove('is-open');
    this.video?.pause();
    this.lockScroll(false);
    this.releaseFocusTrap?.();
    this.releaseFocusTrap = null;
    document.removeEventListener('keydown', this.onKeyDown);

    window.setTimeout(() => {
      if (!this.isOpen) {
        this.hidden = true;
        this.setAttribute('aria-hidden', 'true');
      }
    }, 350);

    this.lastActiveElement?.focus?.();
    this.lastActiveElement = null;
  }

  lockScroll(lock) {
    document.documentElement.classList.toggle('is-scroll-locked', lock);
    document.documentElement.toggleAttribute('data-overlay-open', lock);
  }

  prev() {
    if (this.index > 0) this.showStory(this.index - 1);
  }

  next() {
    if (this.index < this.stories.length - 1) {
      this.showStory(this.index + 1);
    } else {
      this.close();
    }
  }

  toggleMute() {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.updateMuteButton();
  }

  updateMuteButton() {
    if (!this.muteBtn || !this.video) return;
    const muted = this.video.muted;
    this.muteBtn.setAttribute(
      'aria-label',
      muted ? this.muteBtn.dataset.labelWhenMuted : this.muteBtn.dataset.labelWhenUnmuted
    );
    this.muteBtn.textContent = muted ? '🔇' : '🔊';
  }

  onKeyDown(event) {
    if (!this.isOpen) return;
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.prev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
      default:
        break;
    }
  }
}

if (!customElements.get('leep-stories-modal')) {
  customElements.define('leep-stories-modal', LeepStoriesModal);
}
